// src/ui/Toasts.tsx (T1): one toast at a time (Direction D). toast("Moved to Kitchen", { undo }) from anywhere;
// <ToastHost/> is rendered once by App. Centred and as wide as its text on desktop (560px at most), full width
// above the tab bar on phone. An undo toast stays 6 s (10 s for a deleted photo, spec 2.10), others 4.2 s.
// The host is a polite live region. Root class: hb-toast.
import { signal } from "@preact/signals";
import { useEffect, useState } from "preact/hooks";

export type ToastOptions = {
  /** Shows an "Undo" button that runs this and dismisses the toast. */
  undo?: () => void;
  /** Another action label and handler (instead of Undo). */
  action?: { label: string; run: () => void };
  /** Milliseconds; default 6000 with an action, else 4200. */
  duration?: number;
};
type ToastState = { id: number; message: string; opts: ToastOptions };

const current = signal<ToastState | null>(null);
let seq = 0;

/** Shows a toast, replacing any toast on screen. Returns a function that dismisses it. */
export function toast(message: string, opts: ToastOptions = {}): () => void {
  const id = ++seq;
  current.value = { id, message, opts };
  return () => {
    if (current.value?.id === id) current.value = null;
  };
}

export function dismissToast(): void {
  current.value = null;
}

export function ToastHost() {
  const t = current.value;
  const [shown, setShown] = useState<ToastState | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!t) {
      setVisible(false);
      return;
    }
    setShown(t);
    const raf = requestAnimationFrame(() => setVisible(true));
    const hasAction = Boolean(t.opts.undo || t.opts.action);
    const timer = window.setTimeout(() => {
      if (current.value?.id === t.id) current.value = null;
    }, t.opts.duration ?? (hasAction ? 6000 : 4200));
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(timer);
    };
  }, [t]);

  const action = shown?.opts.action ?? (shown?.opts.undo ? { label: "Undo", run: shown.opts.undo } : null);
  return (
    <div class={`hb-toast toast${visible && t ? " show" : ""}`} role="status" aria-live="polite" aria-atomic="true">
      {shown ? <span>{shown.message}</span> : null}
      {shown && action ? (
        <button
          type="button"
          tabIndex={visible && t ? 0 : -1}
          onClick={() => {
            action.run();
            current.value = null;
          }}
        >
          {action.label}
        </button>
      ) : null}
    </div>
  );
}
